import { Box, CircularProgress, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import PrimaryButton from '../buttons/primaryButton';
import { useEffect } from 'react';
import ScoreDisplay from '../playerDisplay/scoreDisplay';
import { useNavigate } from 'react-router-dom';
import InputScoreDialog from '../../score/inputScoreDialog';

import { useAuth } from '../../../contexts/authContext';
import { Roles, hasWorkspaceAccess } from '../../../helpers/access';

import { getEvent, getEventScoreMethod } from '../../../features/event/eventSlice';
import GameSectionWins from '../gameSectionWins';
import InputTotalsDialog from '../../score/inputTotalsDialog';

const GameInProgressCard = ({
  event,
  game,
  actions,
  showSectionWinners,
  isPublic,
  bsaNumber,
  portal
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUser } = useAuth();

  const selectedEvent = useSelector((state) => state.event.event);

  useEffect(() => {
    if (portal) return;
    if (!selectedEvent && game && !isPublic && game.event) {
      dispatch(getEvent(game.event));
    }
  }, [selectedEvent, game, dispatch, isPublic, portal]);

  useEffect(() => {
    if (portal) return;
    if (!selectedEvent?.scoringMethod && isPublic && game.event) {
      dispatch(getEventScoreMethod(game.event));
    }
  }, [selectedEvent, dispatch, isPublic, game.event, portal]);

  const { currentUserProfile, currentWorkspace } = useAuth();

  const gameScorer = game.scorer?.id || game.scorer;

  // Determine which event to use (selectedEvent from Redux or passed event prop).
  // Legacy events without a method (or stored 'both') behave like end-by-end.
  // Standalone quick games carry their own scoringMethod on the game doc.
  const activeEvent = portal?.event || selectedEvent || event;
  const scoringMethod =
    portal && (game?.scoringMethod === 'inputfinalscore' || game?.scoringMethod === 'inputtotals')
      ? game.scoringMethod
      : activeEvent?.scoringMethod === 'inputfinalscore' ||
          activeEvent?.scoringMethod === 'inputtotals'
        ? activeEvent.scoringMethod
        : game?.scoringMethod === 'inputfinalscore' || game?.scoringMethod === 'inputtotals'
          ? game.scoringMethod
          : 'endbyend';

  const handleScoreGameClick = () => {
    if (portal) {
      portal.openGame(game.id);
      return;
    }

    if (isPublic) {
      navigate(
        `/public/scoring-game/${game.id}${bsaNumber ? `?bsa=${encodeURIComponent(bsaNumber)}` : ''}`
      );
    } else {
      navigate(`/game/scoring/${game.id}`);
    }
  };

  const renderScoreEntry = () => {
    if (scoringMethod === 'inputtotals') {
      return (
        <InputTotalsDialog
          game={game}
          submitScore={portal?.submitScore}
          savePhoto={portal?.savePhoto}
          onOpen={portal?.onChanged}
          submitting={portal?.busy}
        />
      );
    }

    if (scoringMethod === 'inputfinalscore') {
      return (
        <InputScoreDialog
          game={game}
          submitScore={portal?.submitScore}
          savePhoto={portal?.savePhoto}
          onOpen={portal?.onChanged}
          submitting={portal?.busy}
        />
      );
    }

    return (
      <PrimaryButton color="success" variant="contained" onClick={handleScoreGameClick}>
        Score Game
      </PrimaryButton>
    );
  };

  return (
    <>
      <Stack justifyContent={'space-between'} height="100%" mt={2}>
        <ScoreDisplay game={game} />
        {portal || selectedEvent?.scoringMethod || game?.scoringMethod ? (
          <Box
            display={'flex'}
            flexDirection={'row'}
            alignSelf={'center'}
            justifySelf={'flex-end'}
            mt={2}>
            {portal ||
            hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.SCORER) ||
            (currentUser && currentUser.uid === gameScorer) ? (
              renderScoreEntry()
            ) : isPublic ? (
              renderScoreEntry()
            ) : (
              <PrimaryButton
                color="info"
                variant="contained"
                onClick={() => navigate(`/embed/games/${game.id}`)}>
                View Score Card
              </PrimaryButton>
            )}
          </Box>
        ) : (
          <CircularProgress size={24} sx={{ alignSelf: 'center', mt: 2 }} />
        )}
        {showSectionWinners && <GameSectionWins game={game} />}
      </Stack>
    </>
  );
};

export default GameInProgressCard;
